import { Editor } from '@tiptap/core';
import { JSONContent } from '@tiptap/react';
import { NumberedNodes, getTypeDescription } from './numbering';
import { capitalizeFirstLetter } from '../../../utils/string';
import { Config } from '../../../env';
import { InternalLinkFormat } from './extensions/plugins/InternalLink';

/**
 * Wrap text with Markdown syntax for the given marks.
 */
const renderMarks = (text: string, marks?: JSONContent['marks']): string => {
  if (!marks || marks.length === 0) return text;
  let result = text;
  for (const mark of marks) {
    switch (mark.type) {
      case 'bold':
        result = `**${result}**`;
        break;
      case 'italic':
        result = `*${result}*`;
        break;
      case 'underline':
        result = `<u>${result}</u>`;
        break;
      case 'link':
        result = `[${result}](${mark.attrs?.href || ''})`;
        break;
      case 'superscript':
        result = `<sup>${result}</sup>`;
        break;
      case 'subscript':
        result = `<sub>${result}</sub>`;
        break;
      // highlight: no markdown equivalent, just keep text
    }
  }
  return result;
};

/**
 * Render inline content (text nodes, inline atoms, hard breaks) to Markdown.
 */
const renderInlineContent = (content: JSONContent[] | undefined, numberedNodes: NumberedNodes): string => {
  if (!content) return '';
  return content
    .map((child) => {
      if (child.type === 'text') {
        return renderMarks(child.text || '', child.marks);
      }
      if (child.type === 'internalLink') {
        const numberedNode = numberedNodes.get(child.attrs?.id);
        const typeDesc = getTypeDescription(child.attrs?.type, child.attrs?.format as InternalLinkFormat);
        return `${typeDesc} ${numberedNode?.numbering ?? ''}`;
      }
      if (child.type === 'guideLink') {
        return child.attrs?.docName || '';
      }
      if (child.type === 'standardLink') {
        return child.attrs?.standardName || '';
      }
      if (child.type === 'mathType') {
        const latexParts = child.attrs?.latexParts || [];
        return `$${latexParts.join('') || 'Formel'}$`;
      }
      if (child.type === 'hardBreak') {
        return '  \n';
      }
      if (child.type === 'image') {
        return `![](${child.attrs?.src || ''})`;
      }
      return '';
    })
    .join('');
};

/**
 * Render figcaption content as block-level Markdown, optionally prepending
 * a bold label to the first paragraph.
 * Figcaptions can contain (bulletList|orderedList|paragraph)+.
 */
const renderFigcaption = (
  figcaption: JSONContent | undefined,
  renderNodeFn: (node: JSONContent) => string,
  indent: string,
  label: string,
): string => {
  if (!figcaption?.content) {
    return label ? `<small>\n\n${indent}${label}\n\n</small>` : '';
  }

  const blocks = figcaption.content
    .map((child) => renderNodeFn(child))
    .filter(Boolean);

  if (label && blocks.length > 0) {
    // Prepend label to the first block (which is typically a paragraph)
    const firstWithoutIndent = blocks[0].startsWith(indent) ? blocks[0].slice(indent.length) : blocks[0];
    blocks[0] = `${indent}${label} ${firstWithoutIndent}`;
  } else if (label) {
    blocks.unshift(`${indent}${label}`);
  }

  return `<small>\n\n${blocks.join('\n\n')}\n\n</small>`;
};

/**
 * Render a table node as a GFM Markdown table.
 */
/**
 * Render a table node as a GFM Markdown table.
 * Uses renderNode for each cell child so all content types are supported.
 * Since GFM cells are single-line, multi-line output is collapsed with spaces.
 */
const renderTable = (tableNode: JSONContent, ctx: RenderContext): string => {
  const rows = tableNode.content || [];
  if (rows.length === 0) return '';

  const renderedRows: string[][] = rows.map((row) =>
    (row.content || []).map((cell) => {
      const cellText = (cell.content || [])
        .map((c) => renderNode(c, ctx).replace(/\n+/g, ' ').trim())
        .filter(Boolean)
        .join(' ');
      // Escape pipes inside cell content
      return cellText.replace(/\|/g, '\\|');
    }),
  );

  if (renderedRows.length === 0) return '';

  const maxCols = Math.max(...renderedRows.map((r) => r.length));
  for (const row of renderedRows) {
    while (row.length < maxCols) row.push('');
  }

  const lines: string[] = [];
  lines.push('| ' + renderedRows[0].join(' | ') + ' |');
  lines.push('| ' + renderedRows[0].map(() => '---').join(' | ') + ' |');
  for (let i = 1; i < renderedRows.length; i++) {
    lines.push('| ' + renderedRows[i].join(' | ') + ' |');
  }
  return lines.join('\n');
};

/**
 * Get the ordered list item marker based on the list type attribute.
 */
const getOrderedMarker = (index: number, listType: string): string => {
  switch (listType) {
    case 'a':
      return `${String.fromCharCode(97 + index)}.`;
    case 'I':
      return `${toRoman(index + 1)}.`;
    default:
      return `${index + 1}.`;
  }
};

const toRoman = (num: number): string => {
  const romanNumerals: [number, string][] = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'],
    [100, 'C'], [90, 'XC'], [50, 'L'], [40, 'XL'],
    [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let result = '';
  for (const [value, numeral] of romanNumerals) {
    while (num >= value) {
      result += numeral;
      num -= value;
    }
  }
  return result;
};

interface RenderContext {
  numberedNodes: NumberedNodes;
  config: Config;
  guideId: string;
}

/**
 * Render a single block-level node to Markdown.
 */
const renderNode = (node: JSONContent, ctx: RenderContext, indent: string = ''): string => {
  const { numberedNodes, config, guideId } = ctx;

  switch (node.type) {
    case 'doc':
    case 'document':
      return (node.content || [])
        .map((child) => renderNode(child, ctx, indent))
        .filter(Boolean)
        .join('\n\n');

    case 'heading': {
      const numberedNode = numberedNodes.get(node.attrs?.id);
      const level = node.attrs?.level || 1;
      const prefix = '#'.repeat(level);
      const numbering = numberedNode ? `${numberedNode.numbering} ` : '';
      const text = renderInlineContent(node.content, numberedNodes);
      return `${indent}${prefix} ${numbering}${text}`;
    }

    case 'paragraph': {
      const text = renderInlineContent(node.content, numberedNodes);
      return `${indent}${text}`;
    }

    case 'bulletList':
      return (node.content || [])
        .map((item) => renderListItem(item, ctx, indent, '-'))
        .join('\n');

    case 'orderedList': {
      const listType = node.attrs?.type || '1';
      return (node.content || [])
        .map((item, i) => renderListItem(item, ctx, indent, getOrderedMarker(i, listType)))
        .join('\n');
    }

    case 'image': {
      const src = node.attrs?.src || '';
      const alt = node.attrs?.alt || '';
      return `${indent}![${alt}](${src})`;
    }

    case 'imageFigure': {
      const attrs = node.attrs || {};
      const numberedNode = numberedNodes.get(attrs.id);
      const figcaption = node.content?.[0];
      const lines: string[] = [];

      if (attrs.smallId && attrs.figureId) {
        const url = `${window.location.origin}${config.apiUrl}/guides/${guideId}/figures/${attrs.figureId}/files/${attrs.smallId}/${attrs.smallFilename || 'image'}?type=small`;
        lines.push(`${indent}![](${url})`);
      }

      const label = numberedNode
        ? `**${capitalizeFirstLetter(getTypeDescription('imageFigure'))} ${numberedNode.numbering}**`
        : '';
      const captionMd = renderFigcaption(figcaption, (child) => renderNode(child, ctx, indent), indent, label);
      if (captionMd) lines.push(captionMd);

      return lines.join('\n\n');
    }

    case 'tableFigure': {
      const attrs = node.attrs || {};
      const numberedNode = numberedNodes.get(attrs.id);
      const content = node.content || [];
      const lines: string[] = [];

      const firstCaption = content[0];
      if (firstCaption?.type === 'figcaption') {
        const label = numberedNode
          ? `**${capitalizeFirstLetter(getTypeDescription('tableFigure'))} ${numberedNode.numbering}**`
          : '';
        const captionMd = renderFigcaption(firstCaption, (child) => renderNode(child, ctx, indent), indent, label);
        if (captionMd) lines.push(captionMd);
      }

      const tableNode = content.find((c) => c.type === 'table');
      if (tableNode) {
        lines.push(renderTable(tableNode, ctx));
      }

      const lastCaption = content[content.length - 1];
      if (lastCaption?.type === 'figcaption' && lastCaption !== firstCaption) {
        const captionMd = renderFigcaption(lastCaption, (child) => renderNode(child, ctx, indent), indent, '');
        if (captionMd) lines.push(captionMd);
      }

      return lines.join('\n\n');
    }

    case 'chartFigure': {
      const attrs = node.attrs || {};
      const numberedNode = numberedNodes.get(attrs.id);
      const figcaption = node.content?.find((c) => c.type === 'figcaption');
      const lines: string[] = [];

      lines.push(`${indent}*[Diagram]*`);

      const label = numberedNode
        ? `**${capitalizeFirstLetter(getTypeDescription('chartFigure'))} ${numberedNode.numbering}**`
        : '';
      const captionMd = renderFigcaption(figcaption, (child) => renderNode(child, ctx, indent), indent, label);
      if (captionMd) lines.push(captionMd);

      return lines.join('\n\n');
    }

    case 'chart':
      return '';

    case 'app': {
      const attrs = node.attrs || {};
      const numberedNode = numberedNodes.get(attrs.id);
      const label = numberedNode
        ? `${capitalizeFirstLetter(getTypeDescription('app'))} ${numberedNode.numbering}`
        : '';
      return `${indent}**${`${label} ${attrs.name || ''}`.trim()}**`;
    }

    case 'pageBreak':
      return `${indent}---`;

    case 'table':
      return renderTable(node, ctx);

    case 'figcaption':
      return renderFigcaption(node, (child) => renderNode(child, ctx, indent), indent, '');

    case 'horizontalRule':
      return `${indent}---`;

    case 'blockquote':
      return (node.content || [])
        .map((child) => renderNode(child, ctx, indent + '> '))
        .join('\n');

    default:
      if (node.content) {
        return (node.content || [])
          .map((child) => renderNode(child, ctx, indent))
          .filter(Boolean)
          .join('\n\n');
      }
      return '';
  }
};

/**
 * Render a list item with the given marker (e.g. `-`, `1.`, `a.`).
 */
const renderListItem = (item: JSONContent, ctx: RenderContext, indent: string, marker: string): string => {
  const children = item.content || [];
  const lines: string[] = [];
  const markerIndent = ' '.repeat(marker.length + 1);

  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (i === 0) {
      lines.push(`${indent}${marker} ${renderNode(child, ctx, '').trim()}`);
    } else {
      lines.push(renderNode(child, ctx, indent + markerIndent));
    }
  }
  return lines.join('\n');
};

const downloadText = (text: string, filename: string) => {
  const blob = new Blob([text], { type: 'text/markdown;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * Export the editor content to a Markdown (.md) file.
 */
export const exportEditorToMarkdown = (editor: Editor, config: Config, guideId: string, filename: string, docName: string, docTitle: string) => {
  const numberedNodes = (editor.storage['numberedNodes'] as NumberedNodes) ?? new Map();
  const json = editor.getJSON();

  // Convert attachment marks to link marks
  const convertAttachmentMarks = (node: JSONContent) => {
    if (node.marks) {
      node.marks = node.marks.map((mark) =>
        mark.type === 'attachment'
          ? { type: 'link', attrs: { href: `${window.location.origin}${config.apiUrl}/guides/${guideId}/attachments/${mark.attrs?.attachmentId}/${mark.attrs?.filename}` } }
          : mark,
      );
    }
    node.content?.forEach(convertAttachmentMarks);
  };
  convertAttachmentMarks(json);

  const markdown = renderNode(json, { numberedNodes, config, guideId });
  const title = `${docName} ${docTitle}`.trim();
  const content = title ? `# ${title}\n\n${markdown}` : markdown;
  downloadText(content, filename);
};
